<template>
  <v-layout
    align-center
    justify-center
  >
    <v-flex
      v-if="indicators && indicators._id"
      xs12
      sm8
      md8
    >
      <h1 class="display-1 primary--text">
        {{ $t('message.indicator_list') }}
      </h1>
      <user-info
        v-if="user.isAdmin && farmer._id"
        class="elevation-1 mb-3"
        :user="farmer"
      />

      <v-alert
        :value="formOptions.disableAll && isEmpty(indicators.approvedOn)"
        type="warning"
      >
        {{ $t('message.indicators_not_approved_yet') }}
      </v-alert>

      <v-alert
        :value="!isEmpty(indicators.approvedOn)"
        type="success"
      >
        {{ $t('message.indicators_approved') }}
      </v-alert>

      <v-alert
        :value="indicators.adminNotes && isEmpty(indicators.approvedOn)"
        type="error"
      >
        {{ $t('message.adminNotes') }}:
        <div class="pre-formatted">
          {{ indicators.adminNotes }}
        </div>
      </v-alert>

      <v-jsonschema-form
        ref="dynamicForm"
        :schema="schema"
        :model="indicators"
        :options="formOptions"
        @error="showError"
        @change="change"
      />

      <submit-draft-dialog
        v-if="!user.isAdmin"
        v-model="dialog"
        :enable-submit="!formOptions.disableAll"
        @confirm="submitDraft"
      />

      <v-btn
        color="primary"
        dark
        :to="{ name: 'indicatorslist' }"
      >
        <v-icon
          left
          dark
        >
          fa-arrow-left
        </v-icon>{{ $t('message.back_to_indicators_button') }}
      </v-btn>

      <return-for-corrections-dialog
        v-if="user.isAdmin"
        v-model="setToDraftDialog"
        :admin-notes="indicators.adminNotes"
        :disabled="indicators.isDraft"
        @confirm="setToDraft"
      />

      <approve-dialog
        v-if="user.isAdmin"
        v-model="approveIndicatorsDialog"
        :disabled="indicators.isDraft"
        @confirm="approveIndicators"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import VJsonschemaForm from '@koumoul/vuetify-jsonschema-form'
import feathersClient from '../feathers-client'
import UserInfo from '@/components/admin/UserInfo'
import SubmitDraftDialog from '@/components/user/SubmitDraftDialog'
import ReturnForCorrectionsDialog from '@/components/admin/ReturnForCorrectionsDialog'
import ApproveDialog from '@/components/admin/ApproveDialog'
import TargetIndicatorCommon from '@/mixins/TartgetIndicatorCommon'
import isEmpty from 'lodash/isEmpty'

const formatDate = function (d) {
  return d ? d.split('T')[0] : ''
}

export default {
  name: 'Indicators',
  components: {
    VJsonschemaForm,
    UserInfo,
    SubmitDraftDialog,
    ReturnForCorrectionsDialog,
    ApproveDialog,
  },
  mixins: [TargetIndicatorCommon],
  data() {
    return {
      targets: {},
      indicators: {},
      sch: {},
      formValid: false,
      dialog: false,
      approveIndicatorsDialog: false,
      setToDraftDialog: false,
      farmer: {},
    }
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      schema: (state) => {
        return state.indicatorsSchema
      },
    }),
    formOptions() {
      let opts = {
        debug: false,
        disableAll:
          !this.$store.state.user.isAdmin &&
          this.indicators._id != '' &&
          typeof this.indicators._id === 'string' &&
          !this.indicators.isDraft,
        autoFoldObjects: true,
        accordionMode: 'popout',
      }
      if (!isEmpty(this.targets)) {
        opts.targets = this.flattenObject(this.targets)
      }
      return opts
    },
  },
  created() {
    document.addEventListener('beforeunload', this.beforeUnload)
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
      case 'onIndicatorsUpdated':
        mutation.payload.date = formatDate(mutation.payload.date)
        if (this.$refs.dynamicForm) {
          Object.keys(mutation.payload).forEach((key) => {
            if (!['updatedAt', 'createdAt'].includes(key)) {
              Vue.set(
                this.$refs.dynamicForm.modelWrapper.root,
                key,
                mutation.payload[key]
              )
            }
          })
        }
        if (!this.user.isAdmin && mutation.payload.isDraft === false) {
          //FIXME: the vue-i18n translation doesn't seem to always work here. See https://github.com/kazupon/vue-i18n/issues/184
          this.$store.commit(
            'showSnackbarMessage',
            'Concept ingediend ter goedkeuring.'
          )
        }
        break
      case 'onIndicators':
        const indicators = mutation.payload.data[0]
        console.log(indicators)
        if (indicators === undefined) this.indicators = { date: '', _id: '' }
        else indicators.date = formatDate(indicators.date)

        this.indicators = indicators
        this.$store.dispatch('getTargets', {
          query: {
            userId: indicators.userId,
          },
        })
        if (!this.user.isAdmin) this.getFarmer(this.indicators.userId)
        break
      case 'onTargets':
        const targets = mutation.payload.data[0]
        if (targets) {
          targets.date = formatDate(targets.date)
          this.targets = targets
        }
        break
      }
    })
  },
  mounted() {
    this.indicators = {}
    this.targets = {}
    this.$store.dispatch('getIndicators', {
      query: {
        _id: this.$router.currentRoute.params.indicatorId,
      },
    })
  },
  methods: {
    beforeUnload() {},
    showError(err) {
      window.alert(err)
    },
    change(e) {
      //save the data to the server on change
      const obj = e.key
        .split('.')
        .reverse()
        .reduce((o, k) => ({ [k]: o }), e.model)
      obj._id = this.indicators._id
      obj.userId = this.indicators.userId
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: obj,
        serviceName: 'indicators',
      })
    },
    submitDraft() {
      //save the data to the server on change
      this.indicators.isDraft = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: this.indicators,
        serviceName: 'indicators',
      })
      this.dialog = false
    },
    approveIndicators() {
      this.approveIndicatorsDialog = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: { _id: this.indicators._id, approvedOn: new Date() },
        serviceName: 'indicators',
      })
    },
    setToDraft(notes) {
      this.setToDraftDialog = false
      this.$store.dispatch('saveIndicatorsOrTargets', {
        data: {
          _id: this.indicators._id,
          isDraft: true,
          approvedOn: null,
          adminNotes: notes,
        },
        serviceName: 'indicators',
      })
    },
    getFarmer(userId) {
      if (this.$store.state.user.isAdmin) {
        feathersClient
          .service('users')
          .get(userId)
          .then((res) => {
            this.farmer = res
          })
          .catch((err) => console.log(err))
      }
    },
    isEmpty: isEmpty,
  },
}
</script>
<style lang="less">
.v-subheader.theme--light {
  background-color: #eee;
  box-shadow: 1px 1px 3px #ddd;
  margin-bottom: 0.5em;
}
.vjsf-property {
  display: flex;
  div.target {
    text-align: right;
    margin-left: 10px;
    flex-basis: 100px;
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 500;
    color: var(--v-primary-base);
    text-transform: uppercase;
    margin-top: 20px;
  }
  > div:first-child {
    flex-grow: 1;
  }
}

.pre-formatted {
  white-space: pre;
}
</style>
